import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, MessageCircleHeart } from "lucide-react";
import { SCENES, AGNES } from "@/lib/images";

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const attempt = () => {
      video.play().catch(() => {});
    };
    attempt();
    document.addEventListener("touchstart", attempt, { once: true });
    document.addEventListener("click", attempt, { once: true });
    return () => {
      document.removeEventListener("touchstart", attempt);
      document.removeEventListener("click", attempt);
    };
  }, []);

  return (
    <section className="relative grain min-h-[100svh] overflow-hidden">
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1)    translateX(0%)   translateY(0%); }
          25%  { transform: scale(1.06) translateX(-1%)  translateY(-1%); }
          50%  { transform: scale(1.1)  translateX(1%)   translateY(0.5%); }
          75%  { transform: scale(1.06) translateX(0%)   translateY(1%); }
          100% { transform: scale(1)    translateX(0%)   translateY(0%); }
        }
        .waterfall-img {
          animation: kenburns 22s ease-in-out infinite;
          transform-origin: center center;
        }
      `}</style>

      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={SCENES.heroWaterfall}
          alt="Cascading waterfall in lush greenery"
          className="waterfall-img absolute inset-0 h-full w-full object-cover"
        />
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={SCENES.heroWaterfall}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/agnes-waterfall.mp4" type="video/mp4" />
        </video>
        {/* drifting mist */}
        <div className="mist-layer pointer-events-none absolute inset-x-0 bottom-0 h-2/3" />
        <div className="mist-layer mist-layer--2 pointer-events-none absolute inset-x-0 bottom-0 h-1/2" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/45 to-forest/85" />
        <div className="absolute inset-0 vignette" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col items-center justify-center px-5 py-28 text-center">
        <motion.span
          variants={fade} initial="hidden" animate="show" custom={0}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold/60 bg-black/10 px-5 py-2 text-xs font-medium uppercase tracking-[0.22em] text-gold backdrop-blur-sm"
        >
          ✦ August 22, 2026 ✦
        </motion.span>

        <motion.p variants={fade} initial="hidden" animate="show" custom={1} className="font-hand text-3xl text-cream/90 md:text-4xl">
          One hundred & seven years of
        </motion.p>

        <motion.h1
          variants={fade} initial="hidden" animate="show" custom={2}
          className="mt-1 font-serif text-6xl font-bold leading-[0.95] tracking-tight text-cream drop-shadow-[0_2px_24px_rgba(0,0,0,0.4)] md:text-8xl"
        >
          Agnes <span className="italic text-gold">Murphy</span>
        </motion.h1>

        <motion.p
          variants={fade} initial="hidden" animate="show" custom={3}
          className="mt-7 max-w-2xl text-balance font-serif text-lg italic leading-relaxed text-cream/85 md:text-2xl"
        >
          A life that began a century ago — through war and wonder, from candlelight
          to code. Come celebrate a journey of resilience, warmth, and love.
        </motion.p>

        <motion.div
          variants={fade} initial="hidden" animate="show" custom={4}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <Link
            to="/fan-wall"
            data-testid="hero-fan-wall-button"
            className="group inline-flex h-14 items-center justify-center gap-2 rounded-full border border-cream/70 px-9 text-base font-medium text-cream backdrop-blur-sm transition-all hover:bg-cream hover:text-forest"
          >
            <MessageCircleHeart className="h-5 w-5" />
            Sign the Birthday Card 🎂
          </Link>
        </motion.div>
      </div>

      {/* floating Agnes portrait — desktop only */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: -6 }}
        transition={{ duration: 1, delay: 0.6 }}
        className="absolute bottom-10 right-8 z-10 hidden w-44 animate-float-slow lg:block"
      >
        <div className="rounded-sm bg-cream p-3 pb-10 shadow-2xl">
          <img src={AGNES.hero} alt="Agnes Murphy" className="h-52 w-full object-cover" />
          <p className="absolute bottom-3 left-0 w-full text-center font-hand text-xl text-forest">Agnes 💛</p>
        </div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-cream/70">
        <ChevronDown className="h-7 w-7 animate-bounce" />
      </div>
    </section>
  );
}
